// src/components/tools/business/CRMTool.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Users, Briefcase, Calendar, Phone, Mail, MessageSquare, 
  Plus, Search, Filter, ChevronRight, ArrowLeft, MoreVertical, 
  Edit2, Trash2, CheckCircle, XCircle, DollarSign, 
  TrendingUp, Clock, Layout, List, Save, Loader2,
  User, Building2, MapPin, Tag, ArrowRight,
  ClipboardList, Ticket, HelpCircle, Check, Play, UserPlus, Target,
  FileText, CheckSquare, MessageCircle, AlertTriangle
} from 'lucide-react';
import { toolsService } from '../../../services/toolsService';

// ============================================================
// CONSTANTS & CONFIG
// ============================================================

const ViewState = {
  DASHBOARD: 'DASHBOARD',
  LEADS: 'LEADS',
  CUSTOMERS: 'CUSTOMERS',
  PIPELINE: 'PIPELINE',
  TICKETS: 'TICKETS',
  LEAD_FORM: 'LEAD_FORM',
  CUSTOMER_FORM: 'CUSTOMER_FORM',
  DEAL_FORM: 'DEAL_FORM',
  TICKET_FORM: 'TICKET_FORM',
  DETAIL: 'DETAIL'
};

const DealStages = {
  LEAD: { id: 'LEAD', label: 'Lead', color: 'gray', probability: 10 },
  QUALIFIED: { id: 'QUALIFIED', label: 'Qualified', color: 'blue', probability: 30 },
  PROPOSAL: { id: 'PROPOSAL', label: 'Proposal', color: 'indigo', probability: 60 },
  NEGOTIATION: { id: 'NEGOTIATION', label: 'Negotiation', color: 'purple', probability: 80 },
  WON: { id: 'WON', label: 'Closed Won', color: 'emerald', probability: 100 },
  LOST: { id: 'LOST', label: 'Closed Lost', color: 'red', probability: 0 }
};

const LeadStatus = {
  NEW: { id: 'NEW', label: 'New', color: 'blue' },
  CONTACTED: { id: 'CONTACTED', label: 'Contacted', color: 'indigo' },
  QUALIFIED: { id: 'QUALIFIED', label: 'Qualified', color: 'emerald' },
  DISQUALIFIED: { id: 'DISQUALIFIED', label: 'Disqualified', color: 'red' },
  CONVERTED: { id: 'CONVERTED', label: 'Converted', color: 'gray' }
};

const CustomerTiers = {
  BRONZE: { id: 'BRONZE', label: 'Bronze', color: 'amber' },
  SILVER: { id: 'SILVER', label: 'Silver', color: 'gray' },
  GOLD: { id: 'GOLD', label: 'Gold', color: 'yellow' },
  PLATINUM: { id: 'PLATINUM', label: 'Platinum', color: 'purple' }
};

const TicketStatus = {
  OPEN: { id: 'OPEN', label: 'Open', color: 'blue' },
  IN_PROGRESS: { id: 'IN_PROGRESS', label: 'In Progress', color: 'amber' },
  RESOLVED: { id: 'RESOLVED', label: 'Resolved', color: 'emerald' },
  CLOSED: { id: 'CLOSED', label: 'Closed', color: 'gray' }
};

const ActivityTypes = {
  CALL: { id: 'CALL', label: 'Call', icon: Phone, color: 'blue' },
  EMAIL: { id: 'EMAIL', label: 'Email', icon: Mail, color: 'indigo' },
  MEETING: { id: 'MEETING', label: 'Meeting', icon: Users, color: 'purple' },
  WHATSAPP: { id: 'WHATSAPP', label: 'WhatsApp', icon: MessageCircle, color: 'emerald' },
  TASK: { id: 'TASK', label: 'Task', icon: CheckSquare, color: 'orange' },
  NOTE: { id: 'NOTE', label: 'Note', icon: ClipboardList, color: 'gray' }
};

const DEFAULT_ONBOARDING_STEPS = [
  { id: '1', label: 'Send Welcome Email', completed: false },
  { id: '2', label: 'Sign Contract/Agreement', completed: false },
  { id: '3', label: 'Collect Billing Details', completed: false },
  { id: '4', label: 'Schedule Kick-off Meeting', completed: false },
  { id: '5', label: 'Create User Account', completed: false }
];

// ============================================================
// HELPER COMPONENTS
// ============================================================

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-ZA', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

const StatCard = ({ icon: Icon, label, value, subValue, color = 'indigo', onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>}
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300" />}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-white rounded-2xl border border-gray-100">
    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

// ============================================================
// SUB-COMPONENTS
// ============================================================

const ActivityFeed = ({ activities, onDelete }) => {
  if (!activities?.length) return <p className="text-gray-400 text-sm text-center py-4">No activity history yet.</p>;

  return (
    <div className="space-y-4">
      {activities.map(activity => {
        const TypeConfig = ActivityTypes[activity.type] || ActivityTypes.NOTE;
        const Icon = TypeConfig.icon;
        
        return (
          <div key={activity.id} className="flex gap-4 group">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full bg-${TypeConfig.color}-100 flex items-center justify-center shrink-0`}>
                <Icon className={`w-4 h-4 text-${TypeConfig.color}-600`} />
              </div>
              <div className="w-px h-full bg-gray-100 my-2 group-last:hidden" />
            </div>
            <div className="flex-1 pb-4">
              <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {TypeConfig.label} • {formatDate(activity.date)}
                  </span>
                  <button onClick={() => onDelete(activity.id)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-gray-800 text-sm whitespace-pre-wrap">{activity.notes}</p>
                {activity.dueDate && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded w-fit">
                    <Clock className="w-3 h-3" /> Due: {formatDate(activity.dueDate)}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const KanbanColumn = ({ stage, deals, customers, leads, onDragOver, onDrop, onEdit }) => {
  const totalValue = deals.reduce((sum, deal) => sum + (parseFloat(deal.value) || 0), 0);

  return (
    <div 
      className="flex-1 min-w-[280px] bg-gray-50/50 rounded-2xl p-4 flex flex-col h-full border border-gray-100"
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, stage.id)}
    >
      <div className={`pb-3 mb-3 border-b border-${stage.color}-100`}>
        <div className="flex justify-between items-center mb-1">
          <h3 className={`font-semibold text-${stage.color}-900`}>{stage.label}</h3>
          <span className={`px-2 py-0.5 rounded-full bg-${stage.color}-100 text-${stage.color}-700 text-xs font-medium`}>
            {deals.length}
          </span>
        </div>
        <p className="text-xs text-gray-500 font-medium">{formatCurrency(totalValue)}</p>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 min-h-[200px]">
        {deals.map(deal => {
          const entity = deal.customerId 
            ? customers.find(c => c.id === deal.customerId) 
            : leads.find(l => l.id === deal.leadId);
            
          return (
            <div
              key={deal.id}
              draggable
              onDragStart={(e) => e.dataTransfer.setData("dealId", deal.id)}
              onClick={() => onEdit(deal)}
              className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-indigo-200 cursor-grab active:cursor-grabbing transition-all group"
            >
              <h4 className="font-medium text-gray-900 mb-1">{deal.title}</h4>
              <p className="text-sm font-bold text-gray-700 mb-2">{formatCurrency(deal.value)}</p>
              
              {entity && (
                <div className="flex items-center gap-2 text-xs text-gray-500 pt-2 border-t border-gray-50">
                  <User className="w-3 h-3" />
                  <span className="truncate">{entity.name}</span>
                </div>
              )}
            </div>
          );
        })}
        {deals.length === 0 && (
          <div className="h-24 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center text-gray-400 text-xs">
            Drag deals here
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// FORMS
// ============================================================

const LeadForm = ({ lead, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', source: '', 
    status: 'NEW', score: 0, notes: '',
    ...lead
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
        <h2 className="text-2xl font-bold text-gray-900">{lead?.id ? 'Edit Lead' : 'New Lead'}</h2>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
              {Object.values(LeadStatus).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
        </div>

        <div>
           <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
           <input type="text" value={formData.source} onChange={e => setFormData({...formData, source: e.target.value})} placeholder="e.g. Website, Referral" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
        </div>
        
        <div>
           <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
           <textarea rows={3} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
        </div>

        <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Lead
          </button>
        </div>
      </div>
    </form>
  );
};

const CustomerForm = ({ customer, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', role: '',
    status: 'Active', address: '', type: 'Customer',
    tier: 'BRONZE', onboardingChecklist: DEFAULT_ONBOARDING_STEPS,
    ...customer
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
        <h2 className="text-2xl font-bold text-gray-900">{customer?.id ? 'Edit Customer' : 'New Customer'}</h2>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
            <select value={formData.tier} onChange={e => setFormData({...formData, tier: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
              {Object.values(CustomerTiers).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <input type="text" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea rows={2} value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
        </div>

        <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Customer
          </button>
        </div>
      </div>
    </form>
  );
};

const DealForm = ({ deal, leads, customers, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    title: '', value: '', stage: 'LEAD', leadId: '', customerId: '',
    expectedCloseDate: '', probability: 10, notes: '',
    ...deal
  });

  useEffect(() => {
    if (formData.stage && DealStages[formData.stage]) {
      setFormData(prev => ({ ...prev, probability: DealStages[formData.stage].probability }));
    }
  }, [formData.stage]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
        <h2 className="text-2xl font-bold text-gray-900">{deal?.id ? 'Edit Deal' : 'New Deal'}</h2>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Deal Title *</label>
          <input required type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g. Q3 Website Redesign" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Value (ZAR)</label>
            <input type="number" value={formData.value} onChange={e => setFormData({...formData, value: parseFloat(e.target.value) || 0})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Stage</label>
            <select value={formData.stage} onChange={e => setFormData({...formData, stage: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
              {Object.values(DealStages).map(stage => (
                <option key={stage.id} value={stage.id}>{stage.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer (Optional)</label>
            <select value={formData.customerId} onChange={e => setFormData({...formData, customerId: e.target.value, leadId: ''})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
              <option value="">Select Customer...</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lead (Optional)</label>
            <select value={formData.leadId} onChange={e => setFormData({...formData, leadId: e.target.value, customerId: ''})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
              <option value="">Select Lead...</option>
              {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Close Date</label>
            <input type="date" value={formData.expectedCloseDate} onChange={e => setFormData({...formData, expectedCloseDate: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Probability (%)</label>
            <input type="number" min="0" max="100" value={formData.probability} onChange={e => setFormData({...formData, probability: parseInt(e.target.value)})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Deal
          </button>
        </div>
      </div>
    </form>
  );
};

const TicketForm = ({ ticket, customers, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    subject: '', status: 'OPEN', priority: 'MEDIUM', customerId: '',
    description: '', type: 'SUPPORT',
    ...ticket
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 space-y-6">
       <div className="flex items-center gap-3">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
        <h2 className="text-2xl font-bold text-gray-900">{ticket?.id ? 'Edit Ticket' : 'New Ticket'}</h2>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
         <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
            <input required type="text" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
         </div>
         
         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
               <select value={formData.customerId} onChange={e => setFormData({...formData, customerId: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
                  <option value="">Select Customer...</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ''}</option>)}
               </select>
            </div>
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
               <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
                  {Object.values(TicketStatus).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
               </select>
            </div>
         </div>

         <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea rows={4} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
         </div>

         <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Ticket
          </button>
        </div>
      </div>
    </form>
  );
};

// ============================================================
// MAIN CRM COMPONENT
// ============================================================

const CRMTool = ({ bookId, onBack, customStyling }) => {
  const [view, setView] = useState(ViewState.DASHBOARD);
  
  // Data State
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [activities, setActivities] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Selection State
  const [selectedEntity, setSelectedEntity] = useState(null); // Can be lead, customer, or ticket
  const [entityType, setEntityType] = useState(null); // 'LEAD', 'CUSTOMER'
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'documents'

  // Load Data
  useEffect(() => {
    loadData();
  }, [bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [l, c, d, t, a, inv, quo] = await Promise.all([
        toolsService.getToolData(bookId, 'crm-leads'),
        toolsService.getToolData(bookId, 'crm-customers'),
        toolsService.getToolData(bookId, 'crm-deals'),
        toolsService.getToolData(bookId, 'crm-tickets'),
        toolsService.getToolData(bookId, 'crm-activities'),
        toolsService.getToolData(bookId, 'invoices'),
        toolsService.getToolData(bookId, 'quotations')
      ]);
      setLeads(l);
      setCustomers(c);
      setDeals(d);
      setTickets(t);
      setActivities(a.sort((a,b) => new Date(b.date) - new Date(a.date))); 
      setInvoices(inv);
      setQuotes(quo);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- CRUD HANDLERS ---
  
  const handleSaveLead = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'crm-leads', data);
      await loadData();
      setView(ViewState.LEADS);
      setSelectedEntity(null);
    } catch (err) { alert('Failed to save lead'); } 
    finally { setSaving(false); }
  };

  const handleSaveCustomer = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'crm-customers', data);
      await loadData();
      if(selectedEntity && selectedEntity.id === data.id) {
         setSelectedEntity(data); // Update local state for view
      } else {
         setView(ViewState.CUSTOMERS);
         setSelectedEntity(null);
      }
    } catch (err) { alert('Failed to save customer'); } 
    finally { setSaving(false); }
  };

  const handleUpdateOnboarding = async (customer, itemId) => {
    const updatedChecklist = customer.onboardingChecklist.map(item => 
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );
    const updatedCustomer = { ...customer, onboardingChecklist: updatedChecklist };
    await handleSaveCustomer(updatedCustomer);
  };

  const handleSaveDeal = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'crm-deals', data);
      if (!data.id) {
        await handleLogActivity('NOTE', `Created new deal: ${data.title}`, data.leadId, data.customerId, null);
      }
      await loadData();
      setView(ViewState.PIPELINE);
      setSelectedDeal(null);
    } catch (err) { alert('Failed to save deal'); }
    finally { setSaving(false); }
  };

  const handleSaveTicket = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'crm-tickets', data);
      await loadData();
      setView(ViewState.TICKETS);
      setSelectedTicket(null);
    } catch (err) { alert('Failed to save ticket'); }
    finally { setSaving(false); }
  };

  const handleConvertLead = async (lead) => {
     if(!window.confirm(`Convert ${lead.name} to Customer?`)) return;
     try {
        setSaving(true);
        // Create Customer
        const customerData = {
           name: lead.name,
           email: lead.email,
           phone: lead.phone,
           status: 'Active',
           type: 'Customer',
           tier: 'BRONZE',
           convertedFromLead: lead.id,
           onboardingChecklist: DEFAULT_ONBOARDING_STEPS
        };
        const newCustomer = await toolsService.saveToolData(bookId, 'crm-customers', customerData);
        
        // Update Lead status
        await toolsService.saveToolData(bookId, 'crm-leads', { ...lead, status: 'CONVERTED' });

        // Log
        await handleLogActivity('NOTE', `Converted from lead`, null, newCustomer.id, null);
        
        await loadData();
        alert('Lead Converted Successfully!');
        setView(ViewState.CUSTOMERS);
     } catch(e) { console.error(e); alert('Conversion failed'); }
     finally { setSaving(false); }
  };

  const handleLogActivity = async (type, notes, leadId = null, customerId = null, dealId = null, dueDate = null) => {
    try {
      const activity = {
        type,
        notes,
        dueDate,
        date: new Date().toISOString(),
        leadId,
        customerId,
        dealId
      };
      await toolsService.saveToolData(bookId, 'crm-activities', activity);
      await loadData();
    } catch (err) { console.error(err); }
  };

  const handleDeleteItem = async (collection, id, currentView) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
        await toolsService.deleteToolData(bookId, collection, id);
        await loadData();
        if(currentView) setView(currentView);
    } catch (err) { console.error(err); }
  };

  // --- DERIVED DATA ---
  
  const stats = useMemo(() => {
    const totalPipeline = deals.reduce((acc, d) => acc + (d.stage !== 'LOST' && d.stage !== 'WON' ? parseFloat(d.value) || 0 : 0), 0);
    const wonRevenue = deals.filter(d => d.stage === 'WON').reduce((acc, d) => acc + (parseFloat(d.value) || 0), 0);
    const conversionRate = deals.length ? Math.round((deals.filter(d => d.stage === 'WON').length / deals.length) * 100) : 0;
    
    return { 
      totalPipeline, 
      wonRevenue, 
      conversionRate, 
      activeDeals: deals.filter(d => d.stage !== 'LOST' && d.stage !== 'WON').length,
      totalLeads: leads.length,
      totalCustomers: customers.length
    };
  }, [deals, leads, customers]);

  const onDragOver = (e) => e.preventDefault();
  
  const onDropDeal = async (e, newStageId) => {
    const dealId = e.dataTransfer.getData("dealId");
    const deal = deals.find(d => d.id === dealId);
    if (deal && deal.stage !== newStageId) {
       const updated = { ...deal, stage: newStageId, probability: DealStages[newStageId].probability };
       setDeals(deals.map(d => d.id === dealId ? updated : d)); // Optimistic UI
       try {
         await toolsService.saveToolData(bookId, 'crm-deals', updated);
         await handleLogActivity('NOTE', `Deal moved to ${DealStages[newStageId].label}`, deal.leadId, deal.customerId, deal.id);
       } catch (err) { await loadData(); }
    }
  };

  if (loading) return <div className="flex justify-center h-64 items-center"><Loader2 className="w-10 h-10 animate-spin text-indigo-600" /></div>;

  // ============================================================
  // VIEW RENDERERS
  // ============================================================

  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Target} label="New Leads" value={leads.filter(l => l.status === 'NEW').length} color="blue" onClick={() => setView(ViewState.LEADS)} />
        <StatCard icon={TrendingUp} label="Pipeline Value" value={formatCurrency(stats.totalPipeline)} color="indigo" onClick={() => setView(ViewState.PIPELINE)} />
        <StatCard icon={DollarSign} label="Revenue Won" value={formatCurrency(stats.wonRevenue)} color="emerald" />
        <StatCard icon={Users} label="Total Customers" value={stats.totalCustomers} color="purple" onClick={() => setView(ViewState.CUSTOMERS)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Briefcase className="w-5 h-5 text-indigo-600" /> Recent Deals</h3>
          {deals.length === 0 ? <p className="text-gray-400 text-sm">No deals active.</p> : (
            <div className="space-y-3">
              {deals.slice(0, 5).map(deal => {
                const entity = deal.customerId ? customers.find(c => c.id === deal.customerId) : leads.find(l => l.id === deal.leadId);
                return (
                 <div key={deal.id} onClick={() => { setSelectedDeal(deal); setView(ViewState.DEAL_FORM); }} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 cursor-pointer">
                    <div>
                      <p className="font-medium text-gray-900">{deal.title}</p>
                      <p className="text-xs text-gray-500">{entity?.name || 'Unknown'}</p>
                    </div>
                    <div className="text-right">
                       <p className="font-bold text-gray-900">{formatCurrency(deal.value)}</p>
                       <span className={`text-xs px-2 py-0.5 rounded-full bg-${DealStages[deal.stage]?.color || 'gray'}-100 text-${DealStages[deal.stage]?.color || 'gray'}-700`}>
                         {DealStages[deal.stage]?.label}
                       </span>
                    </div>
                 </div>
              )})}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-indigo-600" /> Recent Activity</h3>
          <div className="max-h-[300px] overflow-y-auto pr-2">
             <ActivityFeed activities={activities.slice(0, 10)} onDelete={(id) => handleDeleteItem('crm-activities', id)} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderLeads = () => (
     <div className="space-y-4">
        <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-gray-100">
           <h2 className="text-lg font-bold text-gray-800 ml-2">Leads</h2>
           <button onClick={() => { setSelectedEntity(null); setView(ViewState.LEAD_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4" /> Add Lead</button>
        </div>
        {leads.length === 0 ? <EmptyState icon={Target} title="No Leads" description="Start by adding potential leads." action={<button onClick={() => setView(ViewState.LEAD_FORM)} className="text-indigo-600">Add Lead</button>} /> : (
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {leads.map(lead => (
                 <div key={lead.id} onClick={() => { setSelectedEntity(lead); setEntityType('LEAD'); setView(ViewState.DETAIL); }} className="bg-white p-5 rounded-2xl border border-gray-100 hover:shadow-lg cursor-pointer">
                    <div className="flex justify-between items-start">
                       <h3 className="font-bold text-gray-900">{lead.name}</h3>
                       <span className={`text-xs px-2 py-1 rounded bg-${LeadStatus[lead.status]?.color || 'gray'}-100 text-gray-700`}>{LeadStatus[lead.status]?.label}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{lead.email}</p>
                    <p className="text-xs text-gray-400 mt-4">Source: {lead.source || 'Unknown'}</p>
                 </div>
              ))}
           </div>
        )}
     </div>
  );

  const renderCustomers = () => (
     <div className="space-y-4">
        <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-gray-100">
           <h2 className="text-lg font-bold text-gray-800 ml-2">Customers</h2>
           <button onClick={() => { setSelectedEntity(null); setView(ViewState.CUSTOMER_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4" /> Add Customer</button>
        </div>
        {customers.length === 0 ? <EmptyState icon={Users} title="No Customers" description="Convert leads or add customers manually." action={<button onClick={() => setView(ViewState.CUSTOMER_FORM)} className="text-indigo-600">Add Customer</button>} /> : (
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customers.map(cust => {
                 const tierConfig = CustomerTiers[cust.tier || 'BRONZE'];
                 return (
                 <div key={cust.id} onClick={() => { setSelectedEntity(cust); setEntityType('CUSTOMER'); setView(ViewState.DETAIL); }} className="bg-white p-5 rounded-2xl border border-gray-100 hover:shadow-lg cursor-pointer relative overflow-hidden">
                    <div className={`absolute top-0 right-0 w-16 h-16 bg-${tierConfig.color}-100 rounded-bl-full -mr-8 -mt-8`} />
                    <div className="flex items-center gap-3 mb-3 relative z-10">
                       <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-bold">{cust.name.charAt(0)}</div>
                       <div>
                          <h3 className="font-bold text-gray-900">{cust.name}</h3>
                          <p className="text-xs text-gray-500">{cust.company}</p>
                       </div>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                       {cust.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-gray-400"/> {cust.email}</div>}
                       {cust.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-gray-400"/> {cust.phone}</div>}
                    </div>
                    <div className={`mt-3 inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-${tierConfig.color}-100 text-${tierConfig.color}-700`}>
                       {tierConfig.label} Member
                    </div>
                 </div>
              )})}
           </div>
        )}
     </div>
  );

  const renderPipeline = () => (
     <div className="h-full flex flex-col overflow-hidden">
        <div className="flex justify-between items-center mb-4 shrink-0">
           <div className="flex items-center gap-4 bg-white px-3 py-1 rounded-lg border border-gray-100">
              <span className="text-sm font-medium text-gray-600">Total Pipeline: <span className="text-indigo-600 font-bold">{formatCurrency(stats.totalPipeline)}</span></span>
           </div>
           <button onClick={() => { setSelectedDeal(null); setView(ViewState.DEAL_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4" /> New Deal</button>
        </div>
        <div className="flex-1 overflow-x-auto overflow-y-hidden pb-4">
           <div className="flex gap-4 h-full min-w-[1200px]">
              {Object.values(DealStages).map(stage => (
                 <KanbanColumn key={stage.id} stage={stage} deals={deals.filter(d => d.stage === stage.id)} customers={customers} leads={leads} onDragOver={onDragOver} onDrop={onDropDeal} onEdit={(d) => { setSelectedDeal(d); setView(ViewState.DEAL_FORM); }} />
              ))}
           </div>
        </div>
     </div>
  );
  
  const renderTickets = () => (
    <div className="space-y-4">
       <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-gray-100">
           <h2 className="text-lg font-bold text-gray-800 ml-2">Support Tickets</h2>
           <button onClick={() => { setSelectedTicket(null); setView(ViewState.TICKET_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4" /> New Ticket</button>
       </div>
       {tickets.length === 0 ? <EmptyState icon={Ticket} title="No Tickets" description="Customer support requests will appear here." /> : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
             {tickets.map(ticket => {
                const customer = customers.find(c => c.id === ticket.customerId);
                return (
                   <div key={ticket.id} onClick={() => { setSelectedTicket(ticket); setView(ViewState.TICKET_FORM); }} className="flex items-center justify-between p-4 border-b border-gray-50 hover:bg-gray-50 cursor-pointer last:border-0">
                      <div>
                         <h4 className="font-medium text-gray-900">{ticket.subject}</h4>
                         <p className="text-xs text-gray-500">{customer?.name || 'Unknown Customer'}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full bg-${TicketStatus[ticket.status]?.color || 'gray'}-100 text-gray-700`}>{TicketStatus[ticket.status]?.label}</span>
                   </div>
                );
             })}
          </div>
       )}
    </div>
  );

  const renderDetailView = () => {
     if(!selectedEntity) return null;
     const isLead = entityType === 'LEAD';
     const relatedDeals = deals.filter(d => isLead ? d.leadId === selectedEntity.id : d.customerId === selectedEntity.id);
     const relatedActivity = activities.filter(a => isLead ? a.leadId === selectedEntity.id : a.customerId === selectedEntity.id);
     
     // Customer specific data
     const clientInvoices = isLead ? [] : invoices.filter(inv => inv.clientId === selectedEntity.id);
     const clientQuotes = isLead ? [] : quotes.filter(q => q.clientId === selectedEntity.id);
     const tierConfig = !isLead ? CustomerTiers[selectedEntity.tier || 'BRONZE'] : null;

     return (
        <div className="max-w-6xl mx-auto p-4 space-y-6">
           <div className="flex items-center gap-3">
              <button onClick={() => setView(isLead ? ViewState.LEADS : ViewState.CUSTOMERS)} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
              <div>
                 <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    {selectedEntity.name}
                    {!isLead && <span className={`px-2 py-0.5 rounded text-xs uppercase bg-${tierConfig.color}-100 text-${tierConfig.color}-700`}>{tierConfig.label}</span>}
                 </h1>
                 <p className="text-sm text-gray-500">{selectedEntity.role} {selectedEntity.company ? `@ ${selectedEntity.company}` : ''}</p>
              </div>
              <div className="ml-auto flex gap-2">
                 {isLead && selectedEntity.status !== 'CONVERTED' && <button onClick={() => handleConvertLead(selectedEntity)} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700">Convert</button>}
                 <button onClick={() => { setView(isLead ? ViewState.LEAD_FORM : ViewState.CUSTOMER_FORM); }} className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Edit</button>
                 <button onClick={() => handleDeleteItem(isLead ? 'crm-leads' : 'crm-customers', selectedEntity.id, isLead ? ViewState.LEADS : ViewState.CUSTOMERS)} className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50">Delete</button>
              </div>
           </div>

           {/* Tabs */}
           {!isLead && (
              <div className="flex border-b border-gray-200">
                 <button onClick={() => setActiveTab('overview')} className={`px-4 py-2 text-sm font-medium ${activeTab === 'overview' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}>Overview</button>
                 <button onClick={() => setActiveTab('documents')} className={`px-4 py-2 text-sm font-medium ${activeTab === 'documents' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}>Documents ({clientInvoices.length + clientQuotes.length})</button>
              </div>
           )}

           {activeTab === 'overview' ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                 {/* Sidebar Info */}
                 <div className="space-y-6">
                    <div className="bg-white rounded-2xl border border-gray-100 p-6">
                       <div className="mb-4">
                          <label className="text-xs text-gray-400 uppercase font-bold">Contact Info</label>
                          <div className="space-y-2 mt-2">
                             {selectedEntity.email && <div className="flex items-center gap-2 text-sm text-gray-600"><Mail className="w-4 h-4 text-gray-400"/> {selectedEntity.email}</div>}
                             {selectedEntity.phone && <div className="flex items-center gap-2 text-sm text-gray-600"><Phone className="w-4 h-4 text-gray-400"/> {selectedEntity.phone}</div>}
                             {selectedEntity.address && <div className="flex items-center gap-2 text-sm text-gray-600"><MapPin className="w-4 h-4 text-gray-400"/> {selectedEntity.address}</div>}
                          </div>
                       </div>
                       {isLead && <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-sm">Status: <strong>{LeadStatus[selectedEntity.status]?.label}</strong></div>}
                    </div>
                    
                    <div className="bg-white rounded-2xl border border-gray-100 p-6">
                        <div className="flex justify-between items-center mb-3">
                           <h3 className="font-bold text-gray-900">Deals</h3>
                           <button onClick={() => { setSelectedDeal({ [isLead ? 'leadId' : 'customerId']: selectedEntity.id }); setView(ViewState.DEAL_FORM); }} className="text-indigo-600 hover:bg-indigo-50 p-1 rounded"><Plus className="w-4 h-4" /></button>
                        </div>
                        {relatedDeals.length === 0 ? <p className="text-sm text-gray-400">No active deals.</p> : (
                           <div className="space-y-2">
                              {relatedDeals.map(d => (
                                 <div key={d.id} onClick={() => { setSelectedDeal(d); setView(ViewState.DEAL_FORM); }} className="p-3 bg-gray-50 rounded-xl hover:bg-gray-100 cursor-pointer">
                                    <p className="font-medium text-sm text-gray-900">{d.title}</p>
                                    <div className="flex justify-between mt-1">
                                       <span className="text-xs font-bold text-indigo-600">{formatCurrency(d.value)}</span>
                                       <span className="text-[10px] text-gray-400">{DealStages[d.stage].label}</span>
                                    </div>
                                 </div>
                              ))}
                           </div>
                        )}
                    </div>
                 </div>

                 {/* Main Feed & Onboarding */}
                 <div className="lg:col-span-2 space-y-6">
                     {!isLead && (
                        <div className="bg-white rounded-2xl border border-gray-100 p-6">
                           <h3 className="font-bold text-gray-900 mb-3 flex items-center justify-between">
                              Onboarding
                              <span className="text-xs font-normal text-gray-500">{selectedEntity.onboardingChecklist?.filter(i => i.completed).length || 0} / {selectedEntity.onboardingChecklist?.length || 0}</span>
                           </h3>
                           <div className="space-y-2">
                              {(selectedEntity.onboardingChecklist || DEFAULT_ONBOARDING_STEPS).map((task) => (
                                 <label key={task.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer border border-transparent hover:border-gray-200 transition-all">
                                    <input 
                                      type="checkbox" 
                                      checked={task.completed} 
                                      onChange={() => handleUpdateOnboarding(selectedEntity, task.id)}
                                      className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300" 
                                    />
                                    <span className={`text-sm ${task.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{task.label}</span>
                                 </label>
                              ))}
                           </div>
                        </div>
                     )}

                     <div className="bg-white rounded-2xl border border-gray-100 p-4">
                        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 border-b border-gray-100">
                           <button onClick={() => handleLogActivity('NOTE', prompt('Enter Note:'), isLead ? selectedEntity.id : null, !isLead ? selectedEntity.id : null)} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm font-medium text-gray-700 whitespace-nowrap"><ClipboardList className="w-4 h-4" /> Note</button>
                           <button onClick={() => handleLogActivity('CALL', prompt('Call Summary:'), isLead ? selectedEntity.id : null, !isLead ? selectedEntity.id : null)} className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg text-sm font-medium text-blue-700 whitespace-nowrap"><Phone className="w-4 h-4" /> Call</button>
                           <button onClick={() => handleLogActivity('EMAIL', prompt('Email Subject/Summary:'), isLead ? selectedEntity.id : null, !isLead ? selectedEntity.id : null)} className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 rounded-lg text-sm font-medium text-indigo-700 whitespace-nowrap"><Mail className="w-4 h-4" /> Email</button>
                           <button onClick={() => handleLogActivity('WHATSAPP', prompt('WhatsApp Message Summary:'), isLead ? selectedEntity.id : null, !isLead ? selectedEntity.id : null)} className="flex items-center gap-2 px-3 py-1.5 bg-green-50 hover:bg-green-100 rounded-lg text-sm font-medium text-green-700 whitespace-nowrap"><MessageCircle className="w-4 h-4" /> WhatsApp</button>
                           <button onClick={() => {
                              const task = prompt('Task Description:');
                              if(task) {
                                 const date = prompt('Due Date (YYYY-MM-DD):');
                                 handleLogActivity('TASK', task, isLead ? selectedEntity.id : null, !isLead ? selectedEntity.id : null, null, date);
                              }
                           }} className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 rounded-lg text-sm font-medium text-orange-700 whitespace-nowrap"><CheckSquare className="w-4 h-4" /> Task</button>
                        </div>
                        <ActivityFeed activities={relatedActivity} onDelete={(id) => handleDeleteItem('crm-activities', id)} />
                     </div>
                 </div>
              </div>
           ) : (
              // Documents Tab
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                 <h3 className="font-bold text-gray-900 mb-4">Financial Documents</h3>
                 {(clientInvoices.length === 0 && clientQuotes.length === 0) ? (
                    <p className="text-gray-400 text-center py-8">No documents found for this client.</p>
                 ) : (
                    <div className="space-y-4">
                       {clientInvoices.length > 0 && (
                          <div>
                             <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Invoices</h4>
                             <div className="space-y-2">
                                {clientInvoices.map(inv => (
                                   <div key={inv.id} className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                                      <div>
                                         <p className="font-medium text-sm text-gray-900">{inv.documentNumber}</p>
                                         <p className="text-xs text-gray-500">{formatDate(inv.issueDate)}</p>
                                      </div>
                                      <div className="text-right">
                                         <p className="font-bold text-sm text-gray-900">{formatCurrency(inv.total)}</p>
                                         <span className="text-[10px] bg-gray-100 px-2 py-0.5 rounded text-gray-600">{inv.status}</span>
                                      </div>
                                   </div>
                                ))}
                             </div>
                          </div>
                       )}
                       {clientQuotes.length > 0 && (
                          <div>
                             <h4 className="text-xs font-bold text-gray-500 uppercase mb-2 mt-4">Quotations</h4>
                             <div className="space-y-2">
                                {clientQuotes.map(quo => (
                                   <div key={quo.id} className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                                      <div>
                                         <p className="font-medium text-sm text-gray-900">{quo.documentNumber}</p>
                                         <p className="text-xs text-gray-500">{formatDate(quo.issueDate)}</p>
                                      </div>
                                      <div className="text-right">
                                         <p className="font-bold text-sm text-gray-900">{formatCurrency(quo.total)}</p>
                                         <span className="text-[10px] bg-gray-100 px-2 py-0.5 rounded text-gray-600">{quo.status}</span>
                                      </div>
                                   </div>
                                ))}
                             </div>
                          </div>
                       )}
                    </div>
                 )}
              </div>
           )}
        </div>
     );
  };

  return (
    <div className="h-full flex flex-col bg-gray-50/50">
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
          <div className="h-6 w-px bg-gray-200" />
          <nav className="flex gap-1 overflow-x-auto">
             <button onClick={() => setView(ViewState.DASHBOARD)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === ViewState.DASHBOARD ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>Dashboard</button>
             <button onClick={() => setView(ViewState.LEADS)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === ViewState.LEADS ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>Leads</button>
             <button onClick={() => setView(ViewState.PIPELINE)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === ViewState.PIPELINE ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>Pipeline</button>
             <button onClick={() => setView(ViewState.CUSTOMERS)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === ViewState.CUSTOMERS ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>Customers</button>
             <button onClick={() => setView(ViewState.TICKETS)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === ViewState.TICKETS ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>Support</button>
          </nav>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto h-full">
          {view === ViewState.DASHBOARD && renderDashboard()}
          {view === ViewState.LEADS && renderLeads()}
          {view === ViewState.CUSTOMERS && renderCustomers()}
          {view === ViewState.PIPELINE && renderPipeline()}
          {view === ViewState.TICKETS && renderTickets()}
          {view === ViewState.DETAIL && renderDetailView()}
          
          {view === ViewState.LEAD_FORM && <LeadForm lead={selectedEntity} onSave={handleSaveLead} onCancel={() => setView(ViewState.LEADS)} saving={saving} />}
          {view === ViewState.CUSTOMER_FORM && <CustomerForm customer={selectedEntity} onSave={handleSaveCustomer} onCancel={() => setView(ViewState.CUSTOMERS)} saving={saving} />}
          {view === ViewState.DEAL_FORM && <DealForm deal={selectedDeal} leads={leads} customers={customers} onSave={handleSaveDeal} onCancel={() => setView(ViewState.PIPELINE)} saving={saving} />}
          {view === ViewState.TICKET_FORM && <TicketForm ticket={selectedTicket} customers={customers} onSave={handleSaveTicket} onCancel={() => setView(ViewState.TICKETS)} saving={saving} />}
        </div>
      </div>
    </div>
  );
};

export default CRMTool;